import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Lang } from "@shared";
import { fetchConsent, type ConsentPageResponse } from "../game/gameApi";

interface Props {
  /** Fixée par la route : /consentement est française, /consent anglaise. */
  lang: Lang;
}

/**
 * Page de consentement du concours.
 *
 * Une ADRESSE PAR LANGUE — /consentement et /consent — plutôt qu'une page qui
 * suivrait la langue de l'application : ce lien peut être copié, imprimé sur
 * une affiche ou envoyé à un parent qui n'a jamais ouvert le jeu. Il doit
 * mener au même texte pour tout le monde.
 *
 * Le texte vient de `content/consent.<lang>.md`, rendu par le serveur. Il n'est
 * PAS modifiable depuis l'administration : un texte de consentement se relit
 * avant d'être publié, et son historique appartient à git.
 *
 * Accessible sans être connecté.
 */
export default function ConsentPage({ lang }: Props) {
  const { t } = useTranslation();
  const [page, setPage] = useState<ConsentPageResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let vivant = true;
    fetchConsent(lang)
      .then((recue) => {
        if (vivant) setPage(recue);
      })
      .catch(() => {
        if (vivant) setError(true);
      });
    return () => {
      vivant = false;
    };
  }, [lang]);

  // La langue de CETTE page peut différer de celle de l'application : on le
  // déclare, pour les lecteurs d'écran et la synthèse vocale.
  return (
    <main className="page page--text" lang={lang}>
      {/* `lng: lang` sur CHAQUE traduction : cette page a une langue fixe,
          donnée par son adresse, qui n'est pas forcément celle choisie dans
          l'application. Sans cela, « Dernière mise à jour » s'affichait en
          français au bas de la page anglaise. */}
      {error && <p className="form__error">{t("consent.unavailable", { lng: lang })}</p>}
      {!error && page === null && (
        <p className="panel__hint">{t("common.loading", { lng: lang })}</p>
      )}

      {page !== null && (
        <>
          {/* Le HTML vient d'un fichier du dépôt, rendu par notre propre
              convertisseur : aucune saisie d'utilisateur ne l'atteint. */}
          <article className="prose" dangerouslySetInnerHTML={{ __html: page.html }} />

          <p className="prose__footer">
            {/* Date au format canadien officiel AAAA-MM-JJ, comme dans le texte
                de consentement lui-même. `toLocaleDateString` était piégeux :
                en anglais il rendait « 9/15/2026 », format américain, sur une
                page qui annonce une date de suppression en AAAA-MM-JJ deux
                lignes plus haut. `updatedAt` arrive en ISO : ses dix premiers
                caractères SONT déjà la date voulue. */}
            {t("consent.updated", { date: page.updatedAt.slice(0, 10), lng: lang })}
            {" · "}
            <span className="prose__version" title={t("consent.versionHelp", { lng: lang })}>
              {page.version}
            </span>
          </p>

          {/* Les deux langues restent joignables depuis chacune : un parent
              peut arriver ici par un lien qui n'est pas dans sa langue. */}
          <p className="prose__footer">
            <a href={lang === "fr" ? "/consent" : "/consentement"}>
              {lang === "fr" ? "Read this page in English" : "Lire cette page en français"}
            </a>
          </p>
        </>
      )}
    </main>
  );
}
